import {
    defineStore,
} from 'pinia'
import {
    useContextStore,
} from '~~/stores/context'
import {
    PROVIDERS,
} from '#shared/providers'

interface CheckDrivePathParams {
    path: string,
    rootFolderId?: string | null,
    propagate?: boolean,
}

interface CheckDrivePathResult {
    exists: boolean,
    isFolder: boolean | null,
    folderId: string | null,
}

export const useGoogleStore = defineStore('google', () => {
    const PROVIDER = PROVIDERS.GOOGLE

    const contextStore = useContextStore()
    contextStore.registerProvider(PROVIDER)

    contextStore.registerDynamicScope(PROVIDER, 'root_path', {
        label: 'Root Folder',
        widget: 'folder-picker',
        fetchOptions: async () => [],
        onIdentityChange: () => {},
    })

    const identityKey = computed(() => {
        return contextStore.getContext(PROVIDER)?.identityKey ?? null
    })

    const identityId = computed(() => {
        const ctx = contextStore.getContext(PROVIDER)
        if (!ctx?.identityKey) {
            return null
        }
        // identityKey is the string key; reports page checks truthiness only
        return ctx.identityKey
    })

    async function fetchFolderInfo(folderId: string): Promise<{ path: string } | undefined> {
        const result = await useRequest({
            label: `fetch google drive folder info`,
            fetch: () => $fetch<{ path: string }>(`/api/google/drive/folder-info`, {
                params: { folderId },
            }),
            evaluate: (data: any) => data,
        }) as { path: string } | undefined
        return result
    }

    async function checkDrivePath(params: CheckDrivePathParams): Promise<CheckDrivePathResult | undefined> {
        const queryParams: Record<string, string> = { path: params.path }
        if (params.rootFolderId) {
            queryParams.rootFolderId = params.rootFolderId
        }
        return await useRequest({
            label: `check google drive path`,
            fetch: () => $fetch<CheckDrivePathResult>(
                '/api/google/drive/check-path',
                { params: queryParams },
            ),
            evaluate: (data: any) => data,
            propagate: params.propagate,
        }) as CheckDrivePathResult | undefined
    }

    return {
        identityKey,
        identityId,
        fetchFolderInfo,
        checkDrivePath,
    }
})
